import { useState, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

interface DeepSearchQuery {
  id: string;
  query: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  answer?: string;
  error?: string;
  report_id?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

interface DeepSearchOptions {
  userQuery: string;
  contacts: any[];
  initiatives: any[];
  events: any[];
  tasks?: any[];
  instructions?: string;
}

export function useDeepSearch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQuery, setCurrentQuery] = useState<DeepSearchQuery | null>(null);
  const supabase = useSupabaseClient();

  const startDeepSearch = useCallback(async (options: DeepSearchOptions): Promise<DeepSearchQuery | null> => {
    setLoading(true);
    setError(null);

    try {
      // Create a new query record in the database
      const { data: queryData, error: insertError } = await supabase
        .from('deep_search_queries')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          query: options.userQuery,
          status: 'pending'
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`Failed to create query: ${insertError.message}`);
      }

      setCurrentQuery(queryData);

      // Start the background processing
      const response = await fetch('/.netlify/functions/deep-search-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryId: queryData.id,
          userQuery: options.userQuery,
          contacts: options.contacts,
          initiatives: options.initiatives,
          events: options.events,
          tasks: options.tasks,
          instructions: options.instructions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start deep search');
      }

      return queryData;
    } catch (err: any) {
      console.error('Deep search error:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const checkStatus = useCallback(async (queryId: string): Promise<DeepSearchQuery | null> => {
    try {
      const response = await fetch(`/.netlify/functions/check-deep-search-status?queryId=${queryId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check status');
      }

      const data = await response.json();
      
      if (data.success) {
        const query: DeepSearchQuery = {
          id: data.queryId,
          query: '', // This will be filled from the database if needed
          status: data.status,
          answer: data.answer,
          error: data.error,
          created_at: '',
          updated_at: '',
          completed_at: data.completed_at
        };
        
        setCurrentQuery(query);
        return query;
      }
      
      return null;
    } catch (err: any) {
      console.error('Status check error:', err);
      setError(err.message);
      return null;
    }
  }, []);

  const clearCurrentQuery = useCallback(() => {
    setCurrentQuery(null);
    setError(null);
  }, []);

  return {
    loading,
    error,
    currentQuery,
    startDeepSearch,
    checkStatus,
    clearCurrentQuery
  };
}
